import React, { useState } from 'react';  // Keep only useState import
import { Calendar, Clock, MapPin, Users, Trophy, UserPlus, Send, Share } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';  // Add this import
import { GameProposal, Player, Location } from '../types';
import { getStatusColor, getStatusText, getGameGenderLabel } from '../utils/formatters';
import { formatGameDate, formatShortDate } from '../utils/dateUtils';
import AddPlayerDirectlyModal from './AddPlayerDirectlyModal';
import { supabase } from '../lib/supabase';
import InvitePlayerModal from './InvitePlayerModal';
import RegistrationPrompt from './RegistrationPrompt';
import LoginForm from './LoginForm';
import { validatePlayerCategory } from '../utils/categoryValidation';  // Add this import

// Add locations to the props interface
interface GameCardProps {
  game: GameProposal;
  onJoinGame: (gameId: string, message: string) => void;
  currentUser: Player | null;
  onRemovePlayer: (gameId: string, playerId: string) => void;
  locations: Location[];
  onGameClick?: (game: GameProposal) => void;
  onMarkComplete?: (gameId: string) => void;
  onAddPlayerDirectly?: (gameId: string, player: Omit<Player, 'id' | 'email' | 'password'>) => void;
  onInvitePlayer: (gameId: string, playerId: string) => void;
  onRegisterPrompt?: () => void;
}

const GameCard: React.FC<GameCardProps> = ({
  game,
  onJoinGame,
  currentUser,
  onRemovePlayer,
  locations,
  onGameClick,
  onMarkComplete,
  onAddPlayerDirectly,
  onInvitePlayer,
  onRegisterPrompt
}) => {
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRegistrationPrompt, setShowRegistrationPrompt] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);

  // Add handleJoinClick function
  const handleJoinClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentUser) {
      setShowRegistrationPrompt(true);
      return;
    }

    try {
      const validation = validatePlayerCategory(game, currentUser);
      if (!validation.isValid) {
        alert(validation.message);
        return;
      }
      onJoinGame(game.id, '');
    } catch (error) {
      console.error('Error validating player category:', error);
      alert('Erro ao validar categoria do jogador');
    }
  };

  // Add this helper function to format time
  const formatTime = (time: string) => {
    return time.split(':').slice(0, 2).join(':');
  };

  // Helper function to get visibility information
  const handleWhatsAppShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    const gameUrl = `${window.location.origin}/game/${game.id}`;
    const isOrganizer = currentUser && currentUser.id === game.organizer_id;

    const message = isOrganizer
      ? `Olá! Estou organizando um jogo de ${game.sport} no dia ${formatShortDate(game.date)} às ${game.startTime}. Quer participar? Mais detalhes em: ${gameUrl}`
      : `Olá! Encontrei este jogo de ${game.sport} no dia ${formatShortDate(game.date)} às ${game.startTime}. Que tal participar? Mais detalhes em: ${gameUrl}`;

    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const getVisibilityInfo = () => {
    if (game.is_public) {
      return {
        type: 'public' as const,
        label: 'Público',
        avatar: null
      };
    }

    // Check if game has groups
    if (game.game_groups && game.game_groups.length > 0) {
      const firstGroup = game.game_groups[0];
      if (firstGroup.groups) {
        return {
          type: 'group' as const,
          label: firstGroup.groups.name,
          avatar: firstGroup.groups.avatar || null
        };
      }
    }

    return {
      type: 'private' as const,
      label: 'Privado',
      avatar: null
    };
  };

  

  const handleInviteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentUser) {
      setShowRegistrationPrompt(true);
      return;
    }
    setShowInviteModal(true);
  };

  // Add these handlers
  const handleRegistrationClick = () => {
    setShowRegistrationPrompt(false);
    onRegisterPrompt?.();
  };

  const handleLoginClick = () => {
    setShowRegistrationPrompt(false);
    setShowLoginForm(true);
  };

  // Ensure game.id is a string
  // Remove both debug useEffects and keep only the essential code
  const gameId = typeof game.id === 'string' ? game.id : String(game.id);

  const isPlayerInGame = currentUser && game.players.some(player => player.id === currentUser.id);
  const isGameCreator = currentUser && game.createdBy.id === currentUser.id;
  const visibilityInfo = getVisibilityInfo();

  const handleMarkComplete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onMarkComplete) {
      onMarkComplete(gameId);
    }
  };

  const handleAddPlayer = async (player: Omit<Player, 'id' | 'email' | 'password'>) => {
    // Simple validation without debug logging
    if (!/^[0-9a-fA-F-]{36}$/.test(gameId)) {
      return;
    }

    if (onAddPlayerDirectly) {
      try {
        // Insert directly into game_temporary_players
        const { data: tempPlayer, error: tempPlayerError } = await supabase
          .from('game_temporary_players')
          .insert([{
            game_id: gameId,
            name: player.name,
            phone: player.phone || null,
            gender: player.gender || null,
            category: player.category || null,
            playing_side: player.playingSide || null,
            created_by: currentUser?.id,
            join_message: `Adicionado por ${currentUser?.name || 'game creator'}`
          }])
          .select()
          .single();

        if (tempPlayerError) {
          console.error('Error creating temporary player:', tempPlayerError);
          throw tempPlayerError;
        }

        // Call onAddPlayerDirectly with the temporary player data
        onAddPlayerDirectly(gameId, {
          ...player,
          id: tempPlayer.id,
          isTemporary: true
        });
        
        setShowAddPlayerModal(false);
      } catch (error) {
        console.error('Error in handleAddPlayer:', error);
      }
    }
  };

  function getCardBackground() {
    // First check game status
    switch (game.status) {
      case 'full':
        return 'bg-gray-100';
      case 'cancelled':
        return 'bg-red-100';
      case 'expired':
        return 'bg-yellow-100';
      default:
        // If status is 'open', use sport-specific background
        return game.sport === 'tennis' ? 'bg-yellow-100' : game.sport === 'padel' ? 'bg-blue-100' : 'bg-green-100';
    }
  };

  // Remove the useEffect block and keep only the location functions
  const getLocationNames = () => {
    if (Array.isArray(game.locations) && game.locations.length > 0) {
      return game.locations.map(locationId => {
        const location = locations?.find(loc => loc.id === locationId);
        return location ? location.name : '';
      }).filter(Boolean).join(' / ');
    }

    const locationId = game.location_id || 
                      game.locationId || 
                      game.location || 
                      (game.locationIds && game.locationIds[0]);
    
    if (!locationId || !locations) {
      return '';
    }
    
    const location = locations.find(loc => loc.id === locationId);
    return location ? location.name : 'Local não encontrado';
  };

  // Helper function to get sport icon
  const getSportIcon = () => {
    const primarySport = game.sport;
    switch (primarySport) {
      case 'padel':
        return <span className="text-sm font-bold text-blue-600 bg-blue-200 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">P</span>;
      case 'beach-tennis':
        return <span className="text-sm font-bold text-green-600 bg-green-200 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">B</span>;
      case 'tennis':
        return <span className="text-sm font-bold text-yellow-600 bg-yellow-200 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">T</span>;
      default:
        return <span className="text-sm font-bold text-blue-600 bg-blue-200 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">P</span>;
    }
  };

  // Helper function to get sport-specific avatar background
  const getSportAvatarBg = () => {
    switch (game.sport) {
      case 'padel':
        return 'bg-blue-200';
      case 'beach-tennis':
        return 'bg-green-200';
      case 'tennis':
        return 'bg-yellow-200';
      default:
        return 'bg-blue-200';
    }
  };

  const getSportColor = (sport: string) => {
    if (sport === 'padel') return 'border-blue-500';
    if (sport === 'tennis') return 'border-yellow-500';
    if (sport === 'beach-tennis') return 'border-green-500';
    return 'border-gray-500';
  };

  return (
    <>
      <div
        className={`game-card bg-white rounded-lg shadow-sm p-3 border-2 ${getSportColor(game.sport)} hover:shadow-md transition-shadow duration-200 cursor-pointer`}
        onClick={() => onGameClick && onGameClick(game)}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              {getSportIcon()}
              <h3 className="font-bold text-gray-800 text-lg">
                {game.sport === 'padel' ? 'Padel' : 
                 game.sport === 'beach-tennis' ? 'Beach Tennis' : 
                 'Tênis'}
              </h3>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(game.status)}`}>
                {getStatusText(game.status)}
              </span>
            </div>
            <p className="text-xs text-gray-500">
              {game.requiredCategories.join(', ')} • {getGameGenderLabel(game.gender)}
            </p>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-2">
              <div>
                <p className="font-semibold text-gray-700 text-sm">{game.createdBy.name}</p>
                <p className="text-xs text-gray-500">Organizador</p>
              </div>
            </div>
          </div>
        </div>

        {/* Separator */}
        <div className="border-t border-gray-200 my-2" />

        {/* Game Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3 text-xs">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-gray-700 font-medium">{formatGameDate(game.date)}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-gray-700 font-medium">{formatTime(game.startTime)} - {formatTime(game.endTime)}</span>
          </div>
          <div className="flex items-start gap-1.5 col-span-1 sm:col-span-2">
            <MapPin className="w-3.5 h-3.5 text-gray-500 mt-0.5" />
            <span className="text-gray-700 font-medium">{getLocationNames() || 'Local a definir'}</span>
          </div>
        </div>

        {/* Players */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Users className="w-4 h-4 text-gray-500" />
            <span className="text-xs font-semibold text-gray-800">
              {game.players.length} / {game.maxPlayers} Jogadores
            </span>
          </div>
          <div className="flex -space-x-2">
            {game.players.map((player, index) => (
              <div
                key={`${player.id}-${index}`}
                className="w-8 h-8 rounded-full border-2 border-white overflow-hidden shadow-sm"
                title={player.name}
              >
                {player.avatar ? (
                  <img
                    src={player.avatar}
                    alt={player.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className={`w-full h-full ${getSportAvatarBg()} flex items-center justify-center`}>
                    <span className="text-xs font-bold text-white">
                      {player.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-4 pt-3 border-t border-gray-200">
          {game.status === 'open' && (
            <div className="flex items-center gap-2">
              {!isPlayerInGame ? (
                <button
                  onClick={handleJoinClick}
                  className="flex-1 bg-blue-600 text-white py-1.5 px-3 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium text-sm"
                >
                  Entrar no Jogo
                </button>
              ) : (
                <div className="flex-1 text-center text-sm text-green-700 font-semibold bg-green-100 py-2 px-4 rounded-lg">
                  Você está dentro!
                </div>
              )}
              <button
                onClick={handleWhatsAppShare}
                className="bg-gray-200 text-gray-700 p-2 rounded-lg hover:bg-gray-300 transition-colors duration-200"
                title="Compartilhar no WhatsApp"
              >
                <Share className="w-4 h-4" />
              </button>
            </div>
          )}
          {isGameCreator && game.status === 'open' && (
            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={(e) => { e.stopPropagation(); setShowAddPlayerModal(true); }}
                className="flex-1 bg-green-600 text-white py-1.5 px-3 rounded-lg hover:bg-green-700 transition-colors duration-200 font-medium text-sm flex items-center justify-center gap-2"
              >
                <UserPlus className="w-4 h-4" />
                Adicionar Jogador
              </button>
            </div>
          )}
        </div>
      </div>

      
      <AddPlayerDirectlyModal
        isOpen={showAddPlayerModal}
        onClose={() => setShowAddPlayerModal(false)}
        onSubmit={handleAddPlayer}
        gameId={gameId}
        currentUser={currentUser}
      />

      <InvitePlayerModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvite={(playerId) => {
          onInvitePlayer(gameId, playerId);
          setShowInviteModal(false);
        }}
        currentUser={currentUser}
      />

      <RegistrationPrompt
        isOpen={showRegistrationPrompt}
        onClose={() => setShowRegistrationPrompt(false)}
        onRegister={handleRegistrationClick}
        onLogin={handleLoginClick}
        message="Para interagir com os jogos, é necessário criar uma conta."
      />

      <LoginForm
        isOpen={showLoginForm}
        onClose={() => setShowLoginForm(false)}
        onSubmit={async (data) => {
          // LoginForm handles its own login logic
          setShowLoginForm(false);
        }}
        onRegisterClick={() => {
          setShowLoginForm(false);
          onRegisterPrompt?.();
        }}
      />
    </>
  );
}

export default GameCard;